import { GridColDef } from '@mui/x-data-grid-premium';
import React from 'react';
import { tableSettingsProps, useTableSettings } from 'src/core/tables/useTableSettings';
import tableHelper from 'src/core/tables/tableHelper';
import { useBillableOrders } from 'src/pages/orders/orderRequests';

export const useBillableOrdersTable = (): [columns: GridColDef[], tableSettings: tableSettingsProps] => {
  const { rows, status } = useBillableOrders();
  const idPath = ['CustomerAccount', 'Key'];
  const columns: GridColDef[] = React.useMemo(
    () => [
      { field: 'CustomerAccount_Key', headerName: 'Customer Account Id', valueGetter: tableHelper.getFieldKeyOrValue },
      { field: 'CustomerAccount_Value', headerName: 'Customer Account', valueGetter: tableHelper.getFieldKeyOrValue, flex: 1 },
      { field: 'SalesOrdersTotalGrossAmount', headerName: 'Sales Orders Total Gross Amount', flex: 1 },
      { field: 'BillableTotalGrossAmount', headerName: 'Billable Total Gross Amount', flex: 1 },
      { field: 'InvoiceTotalGrossAmount', headerName: 'Invoice Total Gross Amount', flex: 1 }
    ],
    []
  );
  const tableName = 'BillableOrdersTable';
  const tableSettings = useTableSettings({ tableName, idPath, columns, rows, status });
  return [columns, tableSettings];
};

export const useBillableOrdersRowsTable = (rows = []): [columns: GridColDef[], tableSettings: tableSettingsProps] => {
  const idPath = ['Id'];
  const columns: GridColDef[] = React.useMemo(
    () => [
      { field: 'Id', headerName: 'Id' },
      { field: 'OrderNr', headerName: 'Order Nr' },
      { field: 'Status', headerName: 'Status', renderCell: tableHelper.renderStatusCell },
      { field: 'View Orders', renderCell: (params) => { return tableHelper.buttonColumn(params, 'View', '/orders/orders/edit?id=', 'OrderNr', 'View Order'); }, headerName: 'View Order' },
      { field: 'CustomerAccount_Value', headerName: 'Customer Account', valueGetter: tableHelper.getFieldKeyOrValue, flex: 1, minWidth: 200 },
      { field: 'CustomerOrderReference', headerName: 'Customer Order Reference' },
      { field: 'Currency', headerName: 'Currency' },
      { field: 'ShippingAddressFullName', headerName: 'Shipping Address Full Name' },
      { field: 'SalesType', headerName: 'Sales Type' },
      { field: 'TaxIncluded', headerName: 'Tax Included', type: 'boolean' },
      { field: 'BillableDiscounts', headerName: 'Billable Discounts', type: 'number' },
      { field: 'BillableCharges', headerName: 'Billable Charges', type: 'number' },
      { field: 'TotalAmountGross', headerName: 'Total Amount Gross' },
      { field: 'TotalChargesGross', headerName: 'Total Charges Gross' },
      { field: 'Discounts', headerName: 'Discounts' },
      { field: 'CreateDate', headerName: 'Create Date' },
      { field: 'TotalBillableAmountGross', headerName: 'Total Billable Amount Gross' },
      { field: 'InvoiceTotalAmountGross', headerName: 'Invoice Total Amount Gross' },
      { field: 'DiscountsNotInvoiced', headerName: 'Discounts Not Invoiced' },
      { field: 'ChargesNotInvoiced', headerName: 'Charges Not Invoiced' },
      { field: 'CustomerAccount_Key', headerName: 'Customer Account Id', valueGetter: tableHelper.getFieldKeyOrValue }
    ],
    []
  );
  const tableName = 'BillableOrdersRowsTable';
  const tableSettings = useTableSettings({ tableName, idPath, columns, rows });
  tableSettings.hasToolbar = false;
  tableSettings.autoHeight = true;
  tableSettings.loading = false;
  tableSettings.hideFooter = true;
  return [columns, tableSettings];
};

export const useBillableItemsRowsTable = (rows = []): [columns: GridColDef[], tableSettings: tableSettingsProps] => {
  const idPath = ['Id'];
  const columns: GridColDef[] = React.useMemo(
    () => [
      { field: 'Id', headerName: 'Id' },
      { field: 'BillableOrderId', headerName: 'Billable Order Id' },
      { field: 'SalesItem_Value', headerName: 'Sales Item', valueGetter: tableHelper.getFieldKeyOrValue, width: 260 },
      { field: 'Discount', headerName: 'Discount' },
      { field: 'InvoiceId', headerName: 'Invoice Id' },
      { field: 'InvoiceNumber', headerName: 'Invoice Number' },
      { field: 'InvoiceStatus', headerName: 'Invoice Status' },
      { field: 'ItemStatus', headerName: 'Item Status' },
      { field: 'PriceGross', headerName: 'Price Gross' },
      { field: 'Quantity', headerName: 'Quantity', flex: 1 },
      { field: 'SalesOrderRowNo', headerName: 'Sales Order Row No' }
    ],
    []
  );
  const tableName = 'BillableItemsRowsTable';
  const tableSettings = useTableSettings({ tableName, idPath, columns, rows });
  tableSettings.hasToolbar = false;
  tableSettings.autoHeight = true;
  tableSettings.loading = false;
  tableSettings.hideFooter = true;
  return [columns, tableSettings];
};
